import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CONVERSATION_OFFER_SEC, type Conversation, type DispositionCode } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useConversation, useConversationActions, useConversations } from '../../lib/api/hooks-conversations.js';
import { Badge, CardHead, ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { relativeTime } from '../../lib/format.js';

/**
 * WhatsApp and Telegram conversations in the workspace (docs/43 §5.3): offered to this agent like a
 * call -- who it is and what they wrote, Accept or Pass, and the time left -- then answered here and
 * closed with an outcome from the same list as calls.
 *
 * Shows only while the agent has a conversation offered or open, so an account without channels
 * sees the workspace exactly as before.
 */

const CHANNEL_LABEL: Record<Conversation['kind'], string> = { whatsapp: 'WhatsApp', telegram: 'Telegram' };
const who = (c: Conversation): string => c.customer_name ?? (c.customer_number ? `+${c.customer_number}` : 'Customer');

export function ConversationsPanel({ tenantId }: { tenantId: string }) {
  const list = useConversations(tenantId, 'mine');
  const rows = list.data?.data ?? [];
  const [open, setOpen] = useState<string | null>(null);
  // Follow the agent's own: a conversation passed on or closed is no longer theirs to show.
  useEffect(() => { if (open && !rows.some((r) => r.id === open)) setOpen(null); }, [rows, open]);
  if (rows.length === 0) return null;

  const offered = rows.filter((r) => r.state === 'offered');
  const active = rows.filter((r) => r.state === 'active');
  const current = open ?? (active.length === 1 && offered.length === 0 ? active[0]!.id : null);

  return (
    <section className="arc-card">
      <CardHead title="Conversations" sub={`${active.length} open${offered.length ? `, ${offered.length} waiting for you` : ''}`} />
      {offered.map((c) => <Offer key={c.id} tenantId={tenantId} c={c} />)}
      {current
        ? <Thread tenantId={tenantId} id={current} onBack={active.length > 1 ? () => setOpen(null) : undefined} />
        : active.length > 0 && (
          <ul className="arc-ws-msg-threads">
            {active.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => setOpen(c.id)} data-unread={c.unanswered > 0 || undefined}>
                  <span className="arc-ws-msg-thread-top">
                    <strong className="arc-truncate">{who(c)}</strong>
                    <span className="arc-subtle arc-num">{relativeTime(c.last_message_at)}</span>
                  </span>
                  <span className="arc-ws-msg-thread-last arc-truncate">{CHANNEL_LABEL[c.kind]} · {c.preview ?? ''}</span>
                  {c.unanswered > 0 && <span className="arc-ws-msg-count">{c.unanswered}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}

/** One offered to this agent: who, what they wrote, and the seconds before it goes to somebody else. */
function Offer({ tenantId, c }: { tenantId: string; c: Conversation }) {
  const { accept, pass } = useConversationActions(tenantId);
  const until = (c.offered_at ? new Date(c.offered_at).getTime() : Date.now()) + CONVERSATION_OFFER_SEC * 1000;
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const left = Math.max(0, Math.round((until - now) / 1000));
  return (
    <div className="arc-card-body" style={{ borderBottom: '1px solid var(--arc-border)' }} role="alert">
      <div className="arc-row" style={{ gap: 8, justifyContent: 'space-between' }}>
        <div className="arc-stack" style={{ gap: 2, minWidth: 0 }}>
          <strong className="arc-truncate">{who(c)}</strong>
          <span className="arc-subtle" style={{ fontSize: 12 }}>
            {CHANNEL_LABEL[c.kind]}{c.queue_name ? ` · ${c.queue_name}` : ''} · {left}s
          </span>
        </div>
        <div className="arc-row" style={{ gap: 6 }}>
          <button type="button" className="arc-btn arc-btn-sm" disabled={pass.isPending} onClick={() => pass.mutate(c.id)}>Pass</button>
          <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" disabled={accept.isPending} onClick={() => accept.mutate(c.id)}>Accept</button>
        </div>
      </div>
      {c.preview && <p className="arc-muted" style={{ margin: '8px 0 0', fontSize: 13 }}>{c.preview}</p>}
      {(accept.error || pass.error) && <ErrorBanner error={accept.error ?? pass.error} />}
    </div>
  );
}

function Thread({ tenantId, id, onBack }: { tenantId: string; id: string; onBack: (() => void) | undefined }) {
  const q = useConversation(tenantId, id);
  const { reply, close, pass } = useConversationActions(tenantId);
  const [text, setText] = useState('');
  const [closing, setClosing] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const messages = q.data?.messages ?? [];
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);
  const c = q.data?.conversation;
  if (!c) return <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 120 }} /></div>;

  const windowShut = c.kind === 'whatsapp' && (!c.reply_until || new Date(c.reply_until).getTime() < Date.now());
  const send = () => {
    const body = text.trim();
    if (!body || windowShut) return;
    reply.mutate({ id, body }, { onSuccess: () => setText('') });
  };

  return (
    <div className="arc-ws-msg-conv">
      <div className="arc-ws-msg-head">
        {onBack && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label="All conversations" title="All conversations" onClick={onBack}>
            <Icon name="chevronLeft" size={15} />
          </button>
        )}
        <span className="arc-stack" style={{ gap: 0, minWidth: 0, flex: 1 }}>
          <strong className="arc-truncate">{who(c)}</strong>
          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
            {CHANNEL_LABEL[c.kind]}{c.customer_number ? ` · +${c.customer_number}` : ''}{c.queue_name ? ` · ${c.queue_name}` : ''}
          </span>
        </span>
        <button type="button" className="arc-btn arc-btn-sm" disabled={pass.isPending} onClick={() => pass.mutate(id)}>Pass on</button>
        <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" onClick={() => setClosing(true)}>Close</button>
      </div>
      <div className="arc-ws-msg-body" role="log" aria-live="polite">
        {messages.map((m) => (
          <div key={m.id} className="arc-ws-msg" data-dir={m.direction}>
            <div className="arc-ws-msg-bubble">
              {m.attachments.map((a, i) => <div key={i} className="arc-subtle">[{a.type}]</div>)}
              {m.body}
            </div>
            <div className="arc-ws-msg-meta arc-subtle">
              {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {m.direction === 'out' && m.author ? ` · ${m.author}` : ''}
              {m.status === 'sending' ? ' · sending' : m.status === 'failed' ? ` · not sent${m.error ? `: ${m.error}` : ''}`
                : m.status === 'read' ? ' · read' : m.status === 'delivered' ? ' · delivered' : ''}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      {closing ? <CloseForm tenantId={tenantId} pending={close.isPending} error={close.error}
        onCancel={() => setClosing(false)} onClose={(outcome, note) => close.mutate({ id, body: { outcome, note } })} /> : (
        <>
          {windowShut && (
            <p className="arc-subtle" style={{ margin: '0 14px 8px', fontSize: 12 }}>
              WhatsApp allows replies for 24 hours after the customer&apos;s last message. Call them, or wait for them to write again.
            </p>
          )}
          {reply.error && <div style={{ padding: '0 14px' }}><ErrorBanner error={reply.error} /></div>}
          <form className="arc-ws-msg-compose" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea className="arc-input" rows={2} aria-label={`Reply to ${who(c)}`} disabled={windowShut}
              placeholder={windowShut ? 'Replies are closed on WhatsApp' : 'Write a reply'} value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <button type="submit" className="arc-btn arc-btn-primary arc-btn-icon" aria-label="Send" title="Send (Enter)"
              disabled={windowShut || !text.trim() || reply.isPending}>
              <Icon name="send" size={15} />
            </button>
          </form>
        </>
      )}
    </div>
  );
}

/** The outcome, from the account's own list (the same as calls'), and a note where it needs one. */
function CloseForm({ tenantId, pending, error, onCancel, onClose }: {
  tenantId: string; pending: boolean; error: unknown; onCancel: () => void; onClose: (outcome: string, note: string | null) => void;
}) {
  const codes = useQuery({
    queryKey: ['disposition-codes', tenantId],
    queryFn: () => api.get<{ data: DispositionCode[] }>(`/v1/tenants/${tenantId}/disposition-codes`),
  });
  const active = (codes.data?.data ?? []).filter((d) => d.is_active);
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const needsNote = active.find((d) => d.code === outcome)?.requires_note ?? false;
  return (
    <form className="arc-card-body" onSubmit={(e) => { e.preventDefault(); if (outcome && (!needsNote || note.trim())) onClose(outcome, note.trim() || null); }}>
      <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {active.map((d) => (
          <button key={d.code} type="button" className="arc-btn arc-btn-sm" aria-pressed={outcome === d.code}
            data-active={outcome === d.code || undefined} onClick={() => setOutcome(d.code)}>
            {d.label}
          </button>
        ))}
      </div>
      <textarea className="arc-input" rows={2} aria-label="Note" maxLength={500}
        placeholder={needsNote ? 'A note is needed for this outcome' : 'Note (optional)'} value={note} onChange={(e) => setNote(e.target.value)} />
      {error ? <ErrorBanner error={error} /> : null}
      <div className="arc-row" style={{ gap: 8, marginTop: 8 }}>
        <button type="submit" className="arc-btn arc-btn-sm arc-btn-primary" disabled={!outcome || (needsNote && !note.trim()) || pending}>Close conversation</button>
        <button type="button" className="arc-btn arc-btn-sm" onClick={onCancel}>Back</button>
        {outcome && <Badge tone="muted">{active.find((d) => d.code === outcome)?.label}</Badge>}
      </div>
    </form>
  );
}
